import { RouterStrategy } from './routerStrategy';

/**
 * Memory-based router strategy that maintains state in memory
 * without affecting the browser URL
 */
export interface MemoryRouterStrategy extends RouterStrategy {
    /**
     * Subscribe to path changes
     * @param callback Function to call when path changes
     * @returns Cleanup function to unsubscribe
     */
    subscribe(callback: (path: string) => void): () => void;
}

/**
 * Creates a memory-based routing strategy that maintains routing state in memory
 * @param initialPath The initial path to start with
 * @returns A memory-based routing strategy
 */
export function createMemoryStrategy(initialPath: string = ''): MemoryRouterStrategy {
    let currentPath = initialPath;
    const subscribers = new Set<(path: string) => void>();

    /**
     * Normalize a path for memory-based routing
     * @param path The path to normalize
     * @returns The normalized path
     */
    const normalizePath = (path: string | null): string => {
        if (path === null) {
            return '';
        }
        return path.startsWith('/') ? path.slice(1) : path;
    };

    /**
     * Notify all subscribers of a path change
     * @param newPath The new path
     */
    const notifySubscribers = (newPath: string) => {
        subscribers.forEach((callback) => callback(newPath));
    };

    return {
        /**
         * Navigate to a path using memory-based routing
         * @param path The path to navigate to (or null for root)
         * @param replace Whether to replace the current entry (ignored in memory strategy)
         */
        navigate(path: string | null, replace?: boolean): void {
            const newPath = normalizePath(path);
            if (newPath !== currentPath) {
                currentPath = newPath;
                notifySubscribers(currentPath);
            }
        },

        /**
         * Get the current path from memory
         * @returns The current path
         */
        getCurrentPath(): string {
            return currentPath;
        },

        /**
         * Create a URL for the given path (returns the path as-is for memory routing)
         * @param path The path to create a URL for
         * @returns The normalized path string
         */
        createUrl(path: string | null): string {
            return normalizePath(path);
        },

        /**
         * Subscribe to path changes
         * @param callback Function to call when path changes
         * @returns Cleanup function to unsubscribe
         */
        subscribe(callback: (path: string) => void): () => void {
            subscribers.add(callback);
            return () => {
                subscribers.delete(callback);
            };
        },
    };
}
